import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, UserPlus, Lock, Mail, CheckCircle,Zap, Users, Shield, Briefcase, Route, Send, AlertCircle } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { userRegister, resendActivationMail, loggedByGoogle } from "../api/api";
import config from '../utils/config';
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

import { useAuth } from '../utils/AuthContext';
import { useGoogleLogin } from "@react-oauth/google";
import { FcGoogle } from "react-icons/fc"; // Google Icon
import { Link } from "react-router-dom";

const Signup = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const { login } = useAuth();

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const navigate = useNavigate();

  // Benefits of joining Tuly
  const benefits = [
    {
      icon: <Zap size={40} className="text-blue-600" />,
      title: "Intelligent Tool Matching",
      description: "Get tailored tool suggestions instantly. Describe your goal—Tuly breaks it down and finds the top tools for each part of your task."
    },
    {
      icon: <Users size={40} className="text-blue-600" />,
      title: "Share. Rate. Empower Others.",
      description: "Your opinion matters. Rate tools, leave reviews, and help the community make better decisions."
    },
    {
      icon: <Route size={40} className="text-blue-600" />,
      title: "Customize Your Discovery Journey",
      description: "Explore tools by category. Suggest what's missing. Revisit past searches, explore new ones, and shape the platform with your contributions."
    }
  ];

  // Registration steps
  const steps = [
    {
      title: "Personal Information",
      fields: ["firstName", "lastName"]
    },
    {
      title: "Account Details",
      fields: ["email"]
    },
    {
      title: "Security",
      fields: ["password", "confirmPassword"]
    },
    {
      title: "Email Verification",
      fields: []
    }
  ];

  // Check password strength
  useEffect(() => {
    if (password) {
      let strength = 0;
      
      // Length check
      if (password.length >= 8) strength += 1;
      
      // Contains uppercase
      if (/[A-Z]/.test(password)) strength += 1;
      
      // Contains lowercase
      if (/[a-z]/.test(password)) strength += 1;
      
      // Contains number
      if (/[0-9]/.test(password)) strength += 1;
      
      // Contains special char
      if (/[^A-Za-z0-9]/.test(password)) strength += 1;
      
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(0);
    }
  }, [password]);

  // Form validation
  const validateCurrentStep = () => {
    // Reset error message
    setErrorMessage('');

    // Validate based on current step
    if (currentStep === 0) {
      if (firstName.trim().length < 2) {
        setErrorMessage('First name must be at least 2 characters long');
        return false;
      }
      if (lastName.trim().length < 2) {
        setErrorMessage('Last name must be at least 2 characters long');
        return false;
      }
    } else if (currentStep === 1) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setErrorMessage('Please enter a valid email address');
        return false;
      }
    } else if (currentStep === 2) {
      if (password.length < 8) {
        setErrorMessage('Password must be at least 8 characters long');
        return false;
      }
      if (password !== confirmPassword) {
        setErrorMessage('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      if (currentStep < steps.length - 1) {
        if (currentStep === 2) {
          // If we're on the security step, register the user
          handleRegistration();
        } else {
          setCurrentStep(currentStep + 1);
        }
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0 && currentStep < 3) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRegistration = async () => {
    setLoading(true);

    try {
      const results = await userRegister(firstName, lastName, email, password);

      if(results.responsecode == 201){
        setRegistrationComplete(true);
        setCurrentStep(3); // Move to email verification step
      } else if(results.responsecode == 204){
        setErrorMessage('This account already exists, you may Login using the same email!');
      }
    } catch (error) {
      setErrorMessage('It seems there is network connection issue, check your network connection!');
    } finally {
      setLoading(false);
    }

  };

  const handleResendEmail = async () => {
    // Add your resend email logic here
    setLoading(true);
    setMessage('');
    setMessageType('');
    try {
      // Call your resend email API
      const results = await resendActivationMail(email);

      if (results.responsecode == 200) {
        setMessage('Activation email sent successfully! Please check your inbox.');
        setMessageType('success');
      } else {
        setMessage(results.message || 'Failed to send activation email. Please try again.');
        setMessageType('error');
      }
      // For now, just show a success message
      setErrorMessage('');
      // You might want to show a success message instead
    } catch (error) {
      setErrorMessage('Failed to resend activation email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const GoogleSignup = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      console.log("Google token:", tokenResponse);
      // send tokenResponse.access_token to backend for verification
    },
    onError: (error) => {
      console.error("Login Failed:", error);
    },
  });

  const handleGoogleLogin = async (userInfo) => {
    console.log()
    const response = await loggedByGoogle(userInfo.given_name, userInfo.family_name, userInfo.email, userInfo.picture );

    if(response.responsecode == 200){
      login(response.token, response.userId, response.email, response.fname, response.lname);
      navigate('/home');
    }
  }

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Progress indicator
  const Progress = () => (
    <div className="flex items-center justify-between mb-8 w-full">
      {steps.map((step, idx) => (
        <React.Fragment key={idx}>
          {/* Step circle */}
          <div className="flex flex-col items-center">
            <div 
              className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${
                idx < currentStep 
                  ? "bg-blue-600 border-blue-600 text-white" 
                  : idx === currentStep 
                    ? "border-blue-600 text-blue-600" 
                    : "border-gray-300 text-gray-400"
              }`}>
              
              {idx < currentStep ? (
                <CheckCircle size={20} />
              ) : idx === currentStep && currentStep === 3 ? (
                <Send size={20} />
              ) : (
                <span>{idx + 1}</span>
              )}
            </div>
            <span className={`text-xs mt-1 ${
              idx <= currentStep ? "text-blue-600 font-medium" : "text-gray-400"
            }`}>
              {step.title}
            </span>
          </div>
          
          {/* Connector line */}
          {idx < steps.length - 1 && (
            <div 
              className={`flex-1 h-0.5 mx-2 ${
                idx < currentStep ? "bg-blue-600" : "bg-gray-300"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="w-full max-w-4xl flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden">
        
        {/* Top section - Header with logo and features slider */}
        <div className="w-full bg-blue-600 p-6 sm:p-8 text-white">
          <div className="flex items-center justify-between">
            {/* Logo on the left */}
            <div className="flex-shrink-0">
              <img 
                src={`${config.IMG_URL}/tuly.png`}
                alt="Tuly" 
                className="h-14 w-auto filter brightness-0 invert"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='120' height='35' viewBox='0 0 120 35'%3e%3ctext x='0' y='28' font-family='Arial' font-size='28' font-weight='bold' fill='white'%3eTuly%3c/text%3e%3c/svg%3e";
                }}
              />
            </div>
            
            {/* Features slider on the right */}
            <div className="flex-1 max-w-md ml-8 relative">
              <div className="flex items-center">
                {/* Previous button */}
                <button
                  onClick={() => setCurrentSlide(prev => prev === 0 ? benefits.length - 1 : prev - 1)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 mr-3 transition-all duration-200 flex-shrink-0"
                >
                  <ChevronLeft size={20} className="text-white" />
                </button>
                
                {/* Current slide content */}
                <div className="flex-1 min-h-[80px] flex items-center">
                  <div className="flex items-center w-full">
                    <div className="bg-white bg-opacity-20 rounded-lg p-2 mr-3 flex-shrink-0">
                      {benefits[currentSlide].icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-white mb-1">{benefits[currentSlide].title}</h3>
                      <p className="text-blue-100 text-xs leading-relaxed line-clamp-2">{benefits[currentSlide].description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Next button */}
                <button
                  onClick={() => setCurrentSlide(prev => prev === benefits.length - 1 ? 0 : prev + 1)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 ml-3 transition-all duration-200 flex-shrink-0"
                >
                  <ChevronRight size={20} className="text-white" />
                </button>
              </div>
              
              {/* Slide indicators */}
              <div className="flex justify-center mt-3 space-x-2">
                {benefits.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      idx === currentSlide 
                        ? 'bg-white' 
                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - Registration Form */}
        <div className="w-full p-6 sm:p-8 lg:p-12 overflow-y-auto">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-1">
            {currentStep === 3 ? "Check Your Email" : "Create your account"}
          </h1>
          <p className="text-gray-600 mb-8">
            {currentStep === 3 
              ? "We've sent you an activation link" 
              : `Step ${currentStep + 1} of ${steps.length}: ${steps[currentStep].title}`
            }
          </p>
          
          <Progress />
          
          <div className="space-y-6">
            {/* Step 1: Personal Information */}
            {currentStep === 0 && (
              <>
                <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-6 sm:space-y-0">
                  <div className="w-full">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div className="w-full">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Step 2: Account Details */}
            {currentStep === 1 && (
              <>
                <div className="space-y-6 max-w-md mx-auto">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                    placeholder="name@company.com"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    We'll send a verification email to this address
                  </p>
                </div>
              </>
            )}
            
            {/* Step 3: Security */}
            {currentStep === 2 && (
              <>
                <div className="space-y-6 max-w-md mx-auto">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                  />
                  
                  {/* Password strength indicator */}
                  {password && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Password strength:</span>
                        <span className="text-xs font-medium">
                          {passwordStrength < 2 && "Weak"}
                          {passwordStrength >= 2 && passwordStrength < 4 && "Medium"}
                          {passwordStrength >= 4 && "Strong"}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            passwordStrength < 2 ? "bg-red-500" :
                            passwordStrength < 4 ? "bg-yellow-500" : "bg-green-500"
                          }`}
                          style={{ width: `${passwordStrength * 20}%` }}
                        ></div>
                      </div>
                      <ul className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-gray-500">
                        <li className={`flex items-center ${password.length >= 8 ? "text-green-600" : ""}`}>
                          <span className={`mr-1 ${password.length >= 8 ? "text-green-600" : "text-gray-400"}`}>•</span> 
                          At least 8 characters
                        </li>
                        <li className={`flex items-center ${/[A-Z]/.test(password) ? "text-green-600" : ""}`}>
                          <span className={`mr-1 ${/[A-Z]/.test(password) ? "text-green-600" : "text-gray-400"}`}>•</span> 
                          Uppercase letter
                        </li>
                        <li className={`flex items-center ${/[a-z]/.test(password) ? "text-green-600" : ""}`}>
                          <span className={`mr-1 ${/[a-z]/.test(password) ? "text-green-600" : "text-gray-400"}`}>•</span> 
                          Lowercase letter
                        </li>
                        <li className={`flex items-center ${/[0-9]/.test(password) ? "text-green-600" : ""}`}>
                          <span className={`mr-1 ${/[0-9]/.test(password) ? "text-green-600" : "text-gray-400"}`}>•</span> 
                          Number
                        </li>
                        <li className={`flex items-center ${/[^A-Za-z0-9]/.test(password) ? "text-green-600" : ""}`}>
                          <span className={`mr-1 ${/[^A-Za-z0-9]/.test(password) ? "text-green-600" : "text-gray-400"}`}>•</span> 
                          Special character
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="space-y-6 max-w-md mx-auto">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                  />
                </div>
                
              </>
            )}

            {/* Step 4: Email Verification */}
            {currentStep === 3 && (
              <div className="text-center py-8">
                <div className="bg-blue-100 rounded-full p-6 w-24 h-24 flex items-center justify-center mx-auto mb-6">
                  <Mail size={48} className="text-blue-600" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Activation Email Sent!
                </h3>
                
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  We've sent an activation link to <span className="font-medium text-blue-600">{email}</span>. 
                  Please check your email and click the link to activate your account.
                </p>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">
                  <p className="font-medium mb-1">Didn't receive the email?</p>
                  <p>Check your spam folder or click the button below to resend the activation email.</p>
                </div>
                
                <div className="space-y-4">

                  {message && (
                    <div className={`p-4 rounded-lg border flex items-start space-x-3 ${
                      messageType === 'success' 
                        ? 'bg-green-50 border-green-200 text-green-800' 
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                      {messageType === 'success' ? (
                        <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <p className="text-sm font-medium">{message}</p>
                    </div>
                  )}
                  <button
                    onClick={handleResendEmail}
                    disabled={loading}
                    className="w-full bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium rounded-lg py-3 px-4 transition-colors flex items-center justify-center">
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600 mr-2"></div>
                        Resending...
                      </>
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        Resend Activation Email
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleGoToLogin}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-3 px-4 transition-colors"
                  >
                    Go to Login Page
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && currentStep !== 3 && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errorMessage}
              </div>
            )}
            
            {/* Success Message for Step 4 */}
            {errorMessage && currentStep === 3 && !errorMessage.includes('Failed') && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                Activation email sent successfully!
              </div>
            )}
            
            {/* Error Message for Step 4 */}
            {errorMessage && currentStep === 3 && errorMessage.includes('Failed') && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errorMessage}
              </div>
            )}
            
            {/* Navigation Buttons - Only show for steps 1-3 */}
            {currentStep < 3 && (
              <div className="space-y-6 max-w-md mx-auto">
              <div className="flex justify-between pt-4">
                <button 
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className={`px-6 py-3 rounded-lg border font-medium transition-colors ${
                    currentStep === 0 
                      ? "border-gray-200 text-gray-300 cursor-not-allowed" 
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}>
                  Back
                </button>
                
                <button 
                  onClick={nextStep}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center">
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : currentStep < 2 ? (
                    <>Continue</>
                  ) : (
                    <>Go!</>
                  )}
                </button>
              </div>
            </div>
            )}

            {/* Login link - Only show for steps 1-3 */}
            {currentStep < 3 && (
              <>
                <div className="flex items-center space-x-3">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="text-gray-500 text-sm">or continue with</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>

                <div className="w-full mt-4">
                  <GoogleLogin
                    onSuccess={(credentialResponse) => {
                      const token = credentialResponse.credential;
                      const userInfo = jwtDecode(token); 
                      console.log(userInfo);
                      handleGoogleLogin(userInfo);
                    }}
                    onError={() => console.log("Login Failed")}
                    useOneTap
                  />
                </div>

                <div className="mt-12 pt-8">
                  <p className="text-gray-600">Already have an account? <Link to="/login" className="text-blue-600"> Sign in </Link>! </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;